import React, { useEffect, useRef } from 'react';

import { ReactEditor, useSlateStatic } from 'slate-react';

import application from 'application';
import serverInterface from 'server-interface';

import internalEventCodes from 'event-system/codes/internal';

import HelptapEditor from '../../helpers';

import nodeControls from '../../node-menu/control-types';

import types from '../types';

import BaseElement from '../base';
import CarouselContext from './context';

import './index.scss';

const EASINGS = {
  linear(progress) {
    return progress;
  },
  ease(progress) {
    if (progress < 0.5) {
      return 4 * progress * progress * progress;
    }
    return 1 - Math.pow(-2 * progress + 2, 3) / 2;
  },
  spring(progress) {
    const overshoot = 1.70158;
    const factor = overshoot + 1;
    return 1 + factor * Math.pow(progress - 1, 3) + overshoot * Math.pow(progress - 1, 2);
  }
};

const TRANSITION_PRESETS = {
  smooth: { easing: 'ease', duration: 450 },
  spring: { easing: 'spring', duration: 550 },
  linear: { easing: 'linear', duration: 400 },
  instant: { easing: 'ease', duration: 0 }
};

function Carousel(props) {

  const element = props.element;
  const properties = element.properties || {};
  const carouselId = properties.carouselId || null;

  const settings = HelptapEditor.carousel.getSettings(element);

  const editor = useSlateStatic();

  const creatingReference = useRef(false);
  const trackReference = useRef(null);

  const holdTimerReference = useRef(null);
  const holdDelayReference = useRef(0);
  const holdDirectionReference = useRef(0);
  const animationFrameReference = useRef(null);

  useEffect(function onLoad() {

    tryCreateCarousel();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carouselId]);

  useEffect(function onUnload() {

    return cleanupInteractions;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function cleanupInteractions() {

    stopHold();
    cancelTrackAnimation();

  }

  async function tryCreateCarousel() {

    if (carouselId !== null) {
      return;
    }

    if (creatingReference.current === true) {
      return;
    }

    creatingReference.current = true;

    try {

      const carousel = await serverInterface.startup.pages.createCarousel(props.parentEntityId);

      const elementPath = ReactEditor.findPath(editor, props.element);

      HelptapEditor.setAttributes(editor, {
        properties: {
          ...properties,
          carouselId: carousel._id
        }
      }, elementPath);

    } catch (exception) {

      application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
        message: 'Failed to create carousel. Please try again!',
        seconds: 5,
        type: 'error'
      });

    } finally {

      creatingReference.current = false;

    }

  }

  async function handleAddImagesClick() {

    const result = await application.domUtility.askFiles({ type: 'image/*', limit: 10 });

    if (result.cancelled === true) {
      return;
    }

    const elementPath = ReactEditor.findPath(editor, props.element);

    HelptapEditor.carousel.appendItems(editor, elementPath, carouselId, result.files);

  }

  function getTrackItems() {

    const track = trackReference.current;

    if (track === null) {
      return [];
    }

    return Array.prototype.slice.call(track.children);

  }

  function getCurrentItemIndex() {

    const track = trackReference.current;
    const items = getTrackItems();
    const scrollLeft = track.scrollLeft;

    let nearestIndex = 0;
    let minDistance = Infinity;

    for (let index = 0; index < items.length; index++) {

      const distance = Math.abs(items[index].offsetLeft - track.offsetLeft - scrollLeft);

      if (distance < minDistance) {
        minDistance = distance;
        nearestIndex = index;
      }

    }

    return nearestIndex;

  }

  function stepTrack(direction) {

    const track = trackReference.current;

    if (track === null) {
      return;
    }

    const items = getTrackItems();

    if (items.length === 0) {
      return;
    }

    let targetIndex = getCurrentItemIndex() + direction;

    if (targetIndex < 0) {
      targetIndex = 0;
    }

    if (targetIndex > items.length - 1) {
      targetIndex = items.length - 1;
    }

    animateTrackTo(track, items[targetIndex].offsetLeft - track.offsetLeft);

  }

  function animateTrackTo(track, target) {

    cancelTrackAnimation();

    const preset = TRANSITION_PRESETS[properties.transition] || TRANSITION_PRESETS.smooth;
    const start = track.scrollLeft;
    const distance = target - start;

    if (preset.duration <= 0 || Math.abs(distance) < 1) {
      instantScroll(track, target);
      return;
    }

    const easing = EASINGS[preset.easing];
    const startTime = performance.now();

    track.style.scrollBehavior = 'auto';
    track.style.scrollSnapType = 'none';

    function runFrame(now) {

      let progress = (now - startTime) / preset.duration;

      if (progress > 1) {
        progress = 1;
      }

      track.scrollLeft = start + distance * easing(progress);

      if (progress < 1) {
        animationFrameReference.current = requestAnimationFrame(runFrame);
        return;
      }

      animationFrameReference.current = null;
      track.style.scrollBehavior = '';
      track.style.scrollSnapType = '';

    }

    animationFrameReference.current = requestAnimationFrame(runFrame);

  }

  function instantScroll(track, target) {

    const previousBehavior = track.style.scrollBehavior;

    track.style.scrollBehavior = 'auto';
    track.scrollLeft = target;
    track.style.scrollBehavior = previousBehavior;

  }

  function cancelTrackAnimation() {

    if (animationFrameReference.current === null) {
      return;
    }

    cancelAnimationFrame(animationFrameReference.current);
    animationFrameReference.current = null;

  }

  function startHold(direction) {

    stopHold();

    stepTrack(direction);

    holdDirectionReference.current = direction;
    holdDelayReference.current = 500;
    holdTimerReference.current = setTimeout(repeatHold, holdDelayReference.current);

  }

  function repeatHold() {

    stepTrack(holdDirectionReference.current);

    holdDelayReference.current = Math.max(120, holdDelayReference.current * 0.8);
    holdTimerReference.current = setTimeout(repeatHold, holdDelayReference.current);

  }

  function stopHold() {

    if (holdTimerReference.current === null) {
      return;
    }

    clearTimeout(holdTimerReference.current);
    holdTimerReference.current = null;

  }

  function renderArrow(direction, label, symbol) {

    const buttonAttributes = {
      key: label,
      type: 'button',
      className: `carousel-arrow carousel-arrow-${label}`,
      contentEditable: false,
      'aria-label': label,
      onMouseDown(event) {
        event.preventDefault();
      },
      onPointerDown(event) {
        event.preventDefault();
        startHold(direction);
      },
      onPointerUp() {
        stopHold();
      },
      onPointerLeave() {
        stopHold();
      },
      onPointerCancel() {
        stopHold();
      }
    };

    return <button {...buttonAttributes}>{symbol}</button>;

  }

  function tryRenderArrows() {

    if (settings.showArrows === false) {
      return null;
    }

    return [renderArrow(-1, 'left', '‹'), renderArrow(1, 'right', '›')];

  }

  function getItemCount() {

    const children = element.children || [];

    let count = 0;

    for (let index = 0; index < children.length; index++) {
      if (children[index].type === types.CAROUSEL_ITEM) {
        count++;
      }
    }

    return count;

  }

  function tryRenderDots() {

    if (settings.showDots === false) {
      return null;
    }

    const itemCount = getItemCount();
    const dots = [];

    for (let index = 0; index < itemCount; index++) {

      let className = 'carousel-dot';

      if (index === 0) {
        className = 'carousel-dot active';
      }

      const dotAttributes = {
        key: index,
        className,
        contentEditable: false
      };

      dots.push(<span {...dotAttributes} />);

    }

    return <div className="carousel-dots" contentEditable={false}>{dots}</div>;

  }

  function renderAddImagesButton() {

    const buttonAttributes = {
      type: 'button',
      className: 'carousel-add-images-button',
      contentEditable: false,
      onMouseDown(event) {
        event.preventDefault();
      },
      onClick() {
        handleAddImagesClick();
      }
    };

    return <button {...buttonAttributes}>+ Add images</button>;

  }

  function renderCarouselContent() {

    const trackAttributes = {
      className: 'carousel-track hide-scrollbar',
      style: HelptapEditor.carousel.getContainerStyleObject(settings),
      ref: trackReference
    };

    const mainAttributes = {
      ...props.attributes,
      className: 'carousel-main'
    };

    return <div {...mainAttributes}>
      <div className="carousel-content-container">
        <div {...trackAttributes}>
          {props.children}
        </div>
        {tryRenderArrows()}
      </div>
      {tryRenderDots()}
      {renderAddImagesButton()}
    </div>;

  }

  function renderMain() {

    const baseElementProperties = {
      ...props,
      placeholder: '',
      className: 'carousel-element-main',
      customNodeControls: [nodeControls.CAROUSEL_SETTINGS]
    };

    const contextValue = {
      carouselId,
      captionEnabled: settings.captionEnabled,
      captionPosition: settings.captionPosition,
      captionAppearance: settings.captionAppearance
    };

    return <CarouselContext.Provider value={contextValue}>
      <BaseElement {...baseElementProperties}>
        {renderCarouselContent()}
      </BaseElement>
    </CarouselContext.Provider>;

  }

  return renderMain();

}

export default Carousel;